import { useEffect, useState } from 'react';
import type { InvoiceAttachment } from '@faktorplus/shared';
import { fetchAttachmentObjectUrl } from '../../lib/api-client';
import { LoadingBlock } from '../../components/ui/feedback';
import { Button } from '../../components/ui/button';
import { FileIcon } from '../../components/ui/icons';
import styles from './invoice-detail.module.css';

/**
 * Renders an invoice scan.
 *
 * The download route requires a bearer token, which a plain `<img src>` cannot
 * send, so the bytes are fetched and handed to the element as an object URL.
 * That same object URL then powers "open in a new tab" and the download.
 */
export function AttachmentPreview({ attachment }: { attachment: InvoiceAttachment | undefined }) {
  const objectUrl = useObjectUrl(attachment?.id);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!attachment) {
    return (
      <div className={styles.preview}>
        <div className={styles.previewEmpty}>
          <span className={styles.previewMark} aria-hidden="true">
            ▤
          </span>
          <span>Brak skanu faktury</span>
        </div>
      </div>
    );
  }

  const isPdf = attachment.mimeType === 'application/pdf';

  return (
    <>
      <figure className={styles.preview}>
        <div className={styles.previewBar}>
          <span className={styles.previewName}>{attachment.originalName}</span>
          <span className={styles.previewSize}>{formatFileSize(attachment.sizeBytes)}</span>

          <div className={styles.previewTools}>
            <button
              type="button"
              className={styles.previewTool}
              onClick={() => setIsExpanded(true)}
              disabled={!objectUrl}
            >
              ⛶ Pełny ekran
            </button>
            <a
              className={styles.previewTool}
              href={objectUrl ?? undefined}
              download={attachment.originalName}
            >
              ↓ Pobierz
            </a>
          </div>
        </div>

        <div className={styles.previewStage}>
          {!objectUrl ? (
            <LoadingBlock />
          ) : isPdf ? (
            <iframe
              className={styles.previewFrame}
              // Fit the whole page, not the width: an A4 scaled to a wide frame
              // is cropped vertically, which is exactly how the old preview
              // managed to hide the totals at the bottom of every invoice.
              src={`${objectUrl}#view=Fit&toolbar=0&navpanes=0`}
              title={attachment.originalName}
            />
          ) : (
            <img className={styles.previewImage} src={objectUrl} alt={attachment.originalName} />
          )}
        </div>
      </figure>

      {isExpanded && objectUrl && (
        <Lightbox
          url={objectUrl}
          name={attachment.originalName}
          isPdf={isPdf}
          onClose={() => setIsExpanded(false)}
        />
      )}
    </>
  );
}

/**
 * The scan, reachable from a list row.
 *
 * Payables are read to decide what to pay, and that decision keeps needing the
 * document itself — which until now meant opening the invoice and coming back.
 * The bytes are fetched on the click, never with the page: forty rows would
 * otherwise download forty scans nobody asked to see.
 *
 * Clicks are stopped here because the row underneath navigates.
 */
export function AttachmentQuickView({
  attachment,
  label,
}: {
  attachment: InvoiceAttachment;
  label: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const objectUrl = useObjectUrl(isOpen ? attachment.id : undefined);

  return (
    <span onClick={(event) => event.stopPropagation()}>
      <Button
        variant="soft"
        icon={<FileIcon />}
        isLoading={isOpen && !objectUrl}
        aria-label={`Pokaż skan faktury ${label}`}
        title={attachment.originalName}
        onClick={() => setIsOpen(true)}
      >
        Skan
      </Button>

      {isOpen && objectUrl && (
        <Lightbox
          url={objectUrl}
          name={attachment.originalName}
          isPdf={attachment.mimeType === 'application/pdf'}
          onClose={() => setIsOpen(false)}
        />
      )}
    </span>
  );
}

function Lightbox({
  url,
  name,
  isPdf,
  onClose,
}: {
  url: string;
  name: string;
  isPdf: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={styles.lightbox} role="dialog" aria-modal="true" aria-label={name}>
      <div className={styles.lightboxBar}>
        <span className={styles.previewName}>{name}</span>
        <button type="button" className={styles.lightboxClose} onClick={onClose}>
          Zamknij ✕
        </button>
      </div>
      <div className={styles.lightboxStage}>
        {isPdf ? (
          <iframe className={styles.lightboxFrame} src={`${url}#view=FitH`} title={name} />
        ) : (
          <img className={styles.lightboxImage} src={url} alt={name} />
        )}
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Fetches the blob and revokes the object URL when it is no longer displayed. */
function useObjectUrl(attachmentId: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!attachmentId) {
      setUrl(null);
      return;
    }

    let revoked = false;
    let created: string | null = null;

    void fetchAttachmentObjectUrl(attachmentId)
      .then((objectUrl) => {
        if (revoked) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        created = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => setUrl(null));

    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [attachmentId]);

  return url;
}
